/**
 * Authentication Context
 * Manages user authentication state across the application
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, LoginRequest, RegisterRequest, RegisterResponse, AuthContextType } from '../types/auth';
import * as authService from '../services/authService';
import { startBackgroundRefresh, stopBackgroundRefresh } from '../services/apiClient';

// Create context with undefined initial value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider props
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider Component
 * Wraps the application and provides authentication state
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      try {
        const storedUser = authService.getStoredUser();
        const hasToken = authService.isAuthenticated();

        if (storedUser && hasToken) {
          // Verify token is still valid by fetching user
          try {
            const freshUser = await authService.getMe();
            setUser(freshUser);
            setIsAuthenticated(true);
          } catch (err) {
            // Token invalid, clear storage
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (err) {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Start/stop the background token refresh poll alongside auth state.
  // The poll only fires the actual /auth/refresh API call when the access
  // token is within 5 min of expiry, so an idle session never lets the
  // token reach 0s before the next user action.
  useEffect(() => {
    if (isAuthenticated) {
      startBackgroundRefresh();
    } else {
      stopBackgroundRefresh();
    }
    return () => stopBackgroundRefresh();
  }, [isAuthenticated]);

  /**
   * Login handler
   */
  const login = useCallback(async (data: LoginRequest): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.login(data);
      setUser(response.user);
      setIsAuthenticated(true);
    } catch (err: unknown) {
      const errorMessage = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Login failed. Please try again.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Register handler
   */
  const register = useCallback(async (data: RegisterRequest): Promise<RegisterResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.register(data);
      // Don't auto-login after registration — adults verify email,
      // children wait for parental consent.
      return response;
    } catch (err: unknown) {
      const errorMessage = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Registration failed. Please try again.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout handler
   */
  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, []);

  /**
   * Update user in context
   */
  const updateUser = useCallback((updatedUser: User): void => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, []);

  /**
   * Login with OAuth tokens (used for OAuth callback)
   */
  const loginWithTokens = useCallback(async (accessToken: string, refreshToken: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Store tokens
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Fetch user profile
      const freshUser = await authService.getMe();
      setUser(freshUser);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(freshUser));
    } catch (err: unknown) {
      // Clear tokens on error
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      const errorMessage = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Authentication failed. Please try again.';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Refresh user profile from server (e.g., after subscription change)
   */
  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      const freshUser = await authService.getMe();
      setUser(freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
    } catch {
      // silently fail — user state remains as-is
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Context value
  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    updateUser,
    refreshUser,
    clearError,
    loginWithTokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
